import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { RiEyeLine } from 'react-icons/ri';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../components/TableFilters';
import {
  UserProfileStatus,
  UserProfileStatusColor,
  UserRoles,
} from '../../../../../../../models/users';
import { IUserListItem } from '../../../../../../../services/Users/ListUsersService';

export interface IUser extends IUserListItem {
  formattedDate: string;
}

interface IUserTableProps extends BoxProps {
  users: IUser[];
  currentPage?: number;
  filterBy?: string;
  onChangeFilterBy?: (value: string) => void;
  onFilter?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onPageChange?: (page: number) => void;
  onSort?: (sort: 'cpf' | 'name' | 'phone' | 'email' | 'createdAt') => void;
  order?: 'ASC' | 'DESC';
  sortBy?: string;
  totalPages?: number;
}

export const UserTable = ({
  users,
  currentPage,
  filterBy,
  onChangeFilterBy,
  onFilter,
  onOrder,
  onPageChange,
  onSort,
  order,
  sortBy,
  totalPages,
  ...rest
}: IUserTableProps): JSX.Element => {
  const filterByOptions = useMemo(
    () => [
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'E-mail',
        value: 'email',
      },
      {
        label: 'Telefone',
        value: 'phone',
      },
      {
        label: 'CPF',
        value: 'cpf',
      },
    ],
    [],
  );

  const sortByOptions = useMemo(
    () => [
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'Telefone',
        value: 'phone',
      },
      {
        label: 'E-mail',
        value: 'email',
      },
      {
        label: 'CPF',
        value: 'cpf',
      },
      {
        label: 'Data de cadastro',
        value: 'createdAt',
      },
    ],
    [],
  );

  return (
    <Box {...rest}>
      <TableFilters
        filterBy={filterBy}
        filterByOptions={filterByOptions}
        onChangeFilterBy={onChangeFilterBy}
        onFilter={onFilter}
        onOrder={onOrder}
        onSort={(sort) =>
          !!onSort &&
          onSort(sort as 'cpf' | 'name' | 'phone' | 'email' | 'createdAt')
        }
        order={order}
        sortBy={sortBy}
        sortByOptions={sortByOptions}
      />

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th>Usuário</Th>
            <Th>Tipo</Th>
            <Th>Telefone</Th>
            <Th>E-mail</Th>
            <Th>CPF</Th>
            <Th textAlign="center">Status do perfil</Th>
            <Th textAlign="center">Data de cadastro</Th>
            <Th w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {users.map((user) => (
            <Tr key={user.id}>
              <Td>
                <Box>
                  <Text fontWeight="bold">{user.name}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {user.seller?.name}
                  </Text>
                </Box>
              </Td>

              <Td>{UserRoles[user.role]}</Td>

              <Td>{user.phone}</Td>

              <Td>{user.email}</Td>

              <Td>{user.cpf}</Td>

              <Td textAlign="center">
                <Text
                  color={
                    UserProfileStatusColor[
                      user.confirmPending ? 'PENDING' : 'CONFIRMED'
                    ]
                  }
                >
                  {
                    UserProfileStatus[
                      user.confirmPending ? 'PENDING' : 'CONFIRMED'
                    ]
                  }
                </Text>
              </Td>

              <Td textAlign="center">{user.formattedDate}</Td>

              <Td>
                <LinkButton
                  p={0}
                  to={{
                    pathname: '/users/details',
                    state: {
                      userId: user.id,
                    },
                  }}
                >
                  <Icon as={RiEyeLine} fontSize="16" />
                </LinkButton>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
